import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo
} from "react";
import { useTranslation } from "react-i18next";
import DataTable from "@/components/controls/DataTable/DataTable";
import { displayTotalCurrency, formatNumber } from "@/utils/numbers";
import { DEFAULT_CURRENCY_CODE } from "@/utils/numberFormat";
import { simpleDate, toCalendarDate, toLocalDate } from "@/utils/datetime";
import { useFilteredDeals, useResetFilteredDeals } from "@/pages/deals/hooks";
import {
  DealColumn,
  DealConfidenceLevel,
  DealData,
  DealStatus
} from "@/pages/deals/types";
import { ScoreType } from "@/pages/deals/scoreConfig";
import { getEnumTKey, monetarySortingFn } from "@/utils/strings";
import { tWithFallback } from "@/utils/i18n";
import {
  CellContext,
  ColumnDef,
  Row,
  SortingState,
  TableOptions,
  Updater
} from "@tanstack/react-table";
import { ISOStringFormat } from "date-fns";
import {
  ComputedFields,
  Filters,
  GenericAggregations
} from "@/services/api/types";
import { UserData } from "@/entities/users/types";
import { useUsers } from "@/entities/users/hooks";
import ConfidenceLevelIcon from "@/components/controls/ConfidenceLevelIcon";
import NewQuickView from "@/components/layouts/NewQuickView/NewQuickView";
import { useNewQuickViewDrawer } from "@/services/hooks/entities/useNewQuickViewDrawer";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { useDealsNavigation } from "@/services/hooks/entities/deal/useDealsNavigation";
import {
  TabOption,
  TAB_SEARCH_PARAM,
  getTabSlug
} from "@/components/layouts/DetailedView";
import { addClosingQuarterGroup, quarterSortingFn } from "./quarters";
import {
  buildDateBucketColumn,
  DateBucketMeta,
  dateBucketRank,
  formatDateBucketLabel,
  getDateBucket,
  isLocalDayColumn,
  parseDateBucketId
} from "@/entities/dataTables/monthGrouping";
import {
  buildGroupSummaries,
  dealGroupAggregation,
  groupFieldValues,
  rekeyGroupSummaries,
  supportsGroupSummaries
} from "./GroupSummaries";
import { useCompanies } from "@/pages/companies/hooks";
import { useProducts } from "@/entities/settings/hooks";
import { createEnumSortingFn } from "@/utils/enums";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import { ViewDataConfiguration } from "@/entities/views/types";
import { ColumnData } from "@/entities/dataTables/types";
import { resolveSortField } from "@/entities/dataTables/sorting";
import { DEALS_SORT_FIELD_BY_COLUMN } from "@/pages/deals/helpers";
import {
  GroupRankResolvers,
  statusRank
} from "@/entities/dataTables/groupOrdering";
import { StatusBadge } from "@/components/ui/status-badge";
import { StageBadge } from "@/components/ui/stage-badge";
import {
  DealScoreBadge,
  DealScoreBadgeProps
} from "@/pages/deals/components/DealScoreBadge";
import { scoreRank } from "@/pages/deals/scoreFilterOptions";
import TableNameCell from "@/components/controls/TableNameCell";
import IconContainer from "@/components/ui/icon-container";
import { Alert } from "@dreamhub/designsystem/icons";
import {
  SystemCurrencyCell,
  TermsCurrencyCell,
  systemCurrencyHeader,
  termsCurrencyHeader,
  useTermsColumnsVisibleByDefault
} from "@/entities/dataTables/currencyColumns";
import { useAccountCurrency } from "@/services/hooks/useCurrencyConversion";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import AlertsIndication from "@/pages/deals/components/KanbanBoard/DealCard/AlertsIndication";
import { useDealStages } from "@/entities/settings/deal/hooks";
import { APIFieldItem } from "@/pages/settings/types";
import { useFieldVisibility } from "@/pages/settings/hooks";
import {
  customFieldDateGrouping,
  mapSelectedCustomColumnsToTableColumns,
  getTempView
} from "@/entities/dataTables/helpers";
import { CurrencyGroupCode } from "@/entities/dataTables/currencyGrouping";
import { capitalize, Dictionary, isEmpty, keyBy } from "lodash";
import DealsBottomKPIBar from "@/components/controls/BottomKPIBar/DealsBottomKPIBar";
import { usePeople } from "@/entities/people/hooks";
import { getFullName } from "@/entities/people/helpers";
import { isReservedPersonId, PersonData } from "@/entities/people/types";
import { camelizeFieldKey } from "@/utils/fieldKeys";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";

const EMPTY_IDS: string[] = [];

// Row keys must not be the row's index: the stage reorder moves rows, and an index-derived key remounts every displaced row on each page load.
const DEALS_TABLE_OPTIONS: Partial<TableOptions<DealData>> = {
  getRowId: (deal) => deal.id
};

interface Props {
  filters: Filters;
  setFilters?: (filters: Filters | ((prev: Filters) => Filters)) => void;
  filterOptions?: DataTableFilter[];
  searchTerm: string;
  resetPagination?: boolean;
  onPaginationReset?: () => void;
  groupBy?: string[];
  sorting: SortingState;
  onSortingChange: (updater: Updater<SortingState>) => void;
  tableId: string;
  selectedView: ViewDataConfiguration;
  columnsState?: ColumnData[];
  onColumnsChange?: (updatedColumns?: ColumnData[]) => void;
  customFields?: APIFieldItem[];
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  onClearSearch?: () => void;
  customEmptyState?: React.ReactNode;
  // Reports "loaded, and there are no rows" so a parent can drop chrome that
  // makes no sense over an empty table. DealsTable owns the fetch, so the
  // count is not otherwise visible from outside.
  onEmptyChange?: (isEmpty: boolean) => void;
  className?: string;
  viewReady?: boolean;
}

type DealStatusColorMap = {
  [K in DealStatus]: string;
} & { null: string };

export const dealStatusColors: DealStatusColorMap = {
  [DealStatus.IN_PROGRESS]: "var(--natural)",
  [DealStatus.STUCK]: "var(--charts-warning)",
  [DealStatus.CLOSED_WON]: "var(--charts-positive)",
  [DealStatus.CLOSED_LOST]: "var(--charts-negative)",
  null: "var(--border-field-default)"
};

const HARDCODED_DEAL_COLUMN_IDS = new Set<string>(Object.values(DealColumn));

// Registry rows that a built-in column already renders, but whose camelCased
// key does not equal that column's id — the TCV/ACV columns predate the
// registry rows and their ids are load-bearing in saved layouts, so they can't
// be renamed to match. Without reserving the keys here, nothing stops the rows
// being offered a second time as custom columns alongside the built-ins.
export const DEALS_BUILTIN_OWNED_REGISTRY_KEYS: ReadonlySet<string> =
  new Set<string>([
    "contractsTotalContractValue",
    "contractsAnnualContractValue"
  ]);

const DEAL_COLUMN_IDS_OWNED_BY_BUILTINS = new Set<string>([
  ...HARDCODED_DEAL_COLUMN_IDS,
  ...DEALS_BUILTIN_OWNED_REGISTRY_KEYS
]);

export const excludeBuiltinOwnedDealFields = (
  fields: APIFieldItem[] | undefined
): APIFieldItem[] | undefined =>
  fields?.filter(
    (field) =>
      !DEALS_BUILTIN_OWNED_REGISTRY_KEYS.has(camelizeFieldKey(field.fieldKey))
  );
const CONTACT_ID_FIELD = "contactId";
// CMRR is the one contract-derived metric with no built-in column pair, so both
// halves reach the table through the registry (column-customizer) instead. The
// generic renderer would print each under a single configured symbol; route them
// through the split cells so the system half shows the account's currency and
// the terms half the contract's own.
// Keys are the camelCased `deals_fields.field_key` from the account registry
// (the column id the generic mapper looks up), NOT the DealData property name —
// a key that matches no registry field silently falls back to a bare "$".
const renderSystemCurrencyCell = (amount: number): ReactNode => (
  <SystemCurrencyCell amount={amount} />
);

const renderTermsCurrencyCell = (amount: number, row: DealData): ReactNode => (
  <TermsCurrencyCell amount={amount} currency={row.contractsCurrency ?? null} />
);

// A deal with no currency of its own is shown in USD on the deal page.
const dealCurrency = (row: DealData): string =>
  row.currency || DEFAULT_CURRENCY_CODE;

const renderDealCurrencyCell = (amount: number, row: DealData): ReactNode => (
  <TermsCurrencyCell amount={amount} currency={dealCurrency(row)} />
);

const CONTRACTS_CMRR_FIELD = "contractsCmrr";
const CONTRACTS_CMRR_TERMS_FIELD = "contractsCmrrLocal";

// Every split money pair on this table, account-currency half first.
const dealsCurrencyColumnPairs = (
  arrOrMrrField: string
): [string, string][] => [
  [arrOrMrrField, `${arrOrMrrField}Local`],
  [DealColumn.CONTRACTS_CARR, DealColumn.CONTRACTS_CARR_TERMS],
  [CONTRACTS_CMRR_FIELD, CONTRACTS_CMRR_TERMS_FIELD],
  [DealColumn.CONTRACTS_TCV, DealColumn.CONTRACTS_TCV_TERMS],
  [DealColumn.CONTRACTS_ACV, DealColumn.CONTRACTS_ACV_TERMS]
];

// The system money columns that reach the table through the registry with no
// split pair. The committed totals are ARR/MRR, so they hold the account's
// currency; the commercials amounts hold the deal's own, as the deal page shows.
const ACCOUNT_CURRENCY_SYSTEM_COLUMNS = ["committedArr", "committedMrr"];
const DEAL_CURRENCY_SYSTEM_COLUMNS = [
  "contractTotalAmount",
  "implementationCost",
  "trainingCost"
];

const DEALS_CURRENCY_FIELD_RESOLVERS: Record<
  string,
  (amount: number, row: DealData) => ReactNode
> = {
  [CONTRACTS_CMRR_FIELD]: renderSystemCurrencyCell,
  [CONTRACTS_CMRR_TERMS_FIELD]: renderTermsCurrencyCell,
  ...Object.fromEntries(
    ACCOUNT_CURRENCY_SYSTEM_COLUMNS.map((id) => [id, renderSystemCurrencyCell])
  ),
  ...Object.fromEntries(
    DEAL_CURRENCY_SYSTEM_COLUMNS.map((id) => [id, renderDealCurrencyCell])
  )
};

/** Group by offers each money column, grouped by the currency it holds. */
export const getDealsCurrencyGroupByOptions = (
  arrOrMrrField: string
): string[] => [
  ...dealsCurrencyColumnPairs(arrOrMrrField).flat(),
  ...ACCOUNT_CURRENCY_SYSTEM_COLUMNS,
  ...DEAL_CURRENCY_SYSTEM_COLUMNS
];

/**
 * The currency each money column holds, which grouping by it groups on: the
 * account's for the account-currency half, the deal's contract's for the terms
 * half, and the deal's own for the commercials amounts.
 */
const dealsCurrencyCodeResolvers = (
  accountCurrency: string | null,
  arrOrMrrField: string
): Record<string, CurrencyGroupCode> => {
  const accountCurrencyCode: CurrencyGroupCode = () => accountCurrency;
  const dealCurrencyCode: CurrencyGroupCode = (row) =>
    dealCurrency(row as DealData);
  return Object.fromEntries([
    ...dealsCurrencyColumnPairs(arrOrMrrField).flatMap(
      ([systemId, termsId]) => [
        [systemId, accountCurrencyCode],
        [termsId, (row: unknown) => (row as DealData).contractsCurrency]
      ]
    ),
    ...ACCOUNT_CURRENCY_SYSTEM_COLUMNS.map((id) => [id, accountCurrencyCode]),
    ...DEAL_CURRENCY_SYSTEM_COLUMNS.map((id) => [id, dealCurrencyCode])
  ]);
};

// The terms half of every split money pair on this table, built-in and
// registry-driven alike. Off by default for an account that has never signed in
// another currency — see useTermsColumnsVisibleByDefault.
/**
 * The header text of every split money column on this table, keyed by column id.
 * The table's column defs and the Columns-management checkboxes both read from
 * here, so a pair can never be labelled one way in the grid and another in the
 * picker — which, for two columns whose only difference is the currency they
 * hold, would leave the user no way to tell them apart.
 */
export const useDealsCurrencyColumnLabels = (
  arrMrrLabel: string,
  arrOrMrrField: string,
  customFields?: APIFieldItem[]
): ReadonlyMap<string, string> => {
  const { t } = useTranslation();
  const accountCurrency = useAccountCurrency();
  const cmrrLabel = useMemo(
    () =>
      customFields?.find(
        (field) => camelizeFieldKey(field.fieldKey) === CONTRACTS_CMRR_FIELD
      )?.label,
    [customFields]
  );
  return useMemo(() => {
    const labels = new Map<string, string>([
      [arrOrMrrField, systemCurrencyHeader(arrMrrLabel, accountCurrency)],
      [`${arrOrMrrField}Local`, termsCurrencyHeader(arrMrrLabel, t)]
    ]);
    const contractMetrics: [DealColumn, DealColumn][] = [
      [DealColumn.CONTRACTS_CARR, DealColumn.CONTRACTS_CARR_TERMS],
      [DealColumn.CONTRACTS_TCV, DealColumn.CONTRACTS_TCV_TERMS],
      [DealColumn.CONTRACTS_ACV, DealColumn.CONTRACTS_ACV_TERMS]
    ];
    contractMetrics.forEach(([systemId, termsId]) => {
      const metricLabel = t(`deals.${systemId}`);
      labels.set(systemId, systemCurrencyHeader(metricLabel, accountCurrency));
      labels.set(termsId, termsCurrencyHeader(metricLabel, t));
    });
    // Unlike CARR/TCV/ACV above, CMRR's label arrives already composed by
    // settingsapi's _compose_currency_labels — composing it again here
    // would double the currency code (e.g. "CMRR (USD) (USD)").
    if (cmrrLabel) {
      labels.set(CONTRACTS_CMRR_FIELD, cmrrLabel);
    }
    return labels;
  }, [arrMrrLabel, arrOrMrrField, accountCurrency, cmrrLabel, t]);
};

export const DEALS_TERMS_COLUMN_IDS: ReadonlySet<string> = new Set<string>([
  DealColumn.CONTRACTS_CARR_TERMS,
  DealColumn.CONTRACTS_TCV_TERMS,
  DealColumn.CONTRACTS_ACV_TERMS,
  CONTRACTS_CMRR_TERMS_FIELD,
  "arrLocal",
  "mrrLocal"
]);

// Built-in columns added after accounts had settled layouts; they start
// unchecked so no account's table changes until someone opts in.
export const DEALS_NEW_COLUMN_IDS: ReadonlySet<string> = new Set<string>([
  DealColumn.LAST_ACTIVITY,
  DealColumn.QUALIFICATION_TEXT,
  DealColumn.STAKEHOLDERS_APPROVAL_TEXT,
  DealColumn.POC_TEXT,
  DealColumn.NEXT_STEP_TEXT
]);

export const getDealsHiddenByDefaultColumnIds = (
  termsColumnsVisibleByDefault: boolean
): ReadonlySet<string> =>
  new Set<string>([
    ...DEALS_NEW_COLUMN_IDS,
    ...(termsColumnsVisibleByDefault ? [] : DEALS_TERMS_COLUMN_IDS)
  ]);

// Custom field types whose column carries no sort.
const UNSORTABLE_CUSTOM_DISPLAY_TYPES: ReadonlySet<FieldDisplayType> = new Set([
  FieldDisplayType.FILE_UPLOAD,
  FieldDisplayType.BUTTON
]);

/**
 * Every deals column the Sort by menu must not offer: the ones whose header
 * carries no sort, so the menu and the header cannot disagree, and the closing
 * quarter, which sorts as the expected close date already listed.
 */
export const getDealsSortMenuExclusions = (
  customFields: APIFieldItem[] | undefined
): ReadonlySet<string> =>
  new Set<string>([
    DealColumn.ALERTS,
    DealColumn.CLOSING_QUARTER,
    ...DEALS_TERMS_COLUMN_IDS,
    ...(customFields ?? [])
      .filter((field) => UNSORTABLE_CUSTOM_DISPLAY_TYPES.has(field.displayType))
      .map((field) => camelizeFieldKey(field.fieldKey))
  ]);

// Each deal KPI column renders its score as the badge of its questionnaire.
const KPI_COLUMNS: {
  column: DealColumn;
  scoreType: ScoreType;
  size?: DealScoreBadgeProps["size"];
}[] = [
  {
    column: DealColumn.KEY_STAKEHOLDERS_MAPPING_TEXT,
    scoreType: ScoreType.STAKEHOLDERS_MAPPING,
    size: "sm"
  },
  {
    column: DealColumn.CHAMPIONS_MAPPING_TEXT,
    scoreType: ScoreType.CHAMPION_SCORE
  },
  { column: DealColumn.QUALIFICATION_TEXT, scoreType: ScoreType.QUALIFICATION },
  {
    column: DealColumn.STAKEHOLDERS_APPROVAL_TEXT,
    scoreType: ScoreType.STAKEHOLDERS_APPROVAL
  },
  { column: DealColumn.POC_TEXT, scoreType: ScoreType.POC }
];

const getColumnKey = (col: ColumnDef<DealData, any>): string =>
  ("accessorKey" in col ? col.accessorKey : col.id) as string;

export const applyColumnMeta = <T extends object>(columns: ColumnDef<T>[]) => {
  return columns.map((column) => ({
    ...column,
    meta: {
      ...column.meta,
      textAlign:
        (column.meta as { textAlign?: string } | undefined)?.textAlign ??
        "start"
    }
  }));
};

const DealsTable: React.FC<Props> = ({
  filters,
  setFilters,
  filterOptions,
  searchTerm,
  resetPagination,
  onPaginationReset,
  groupBy = [],
  sorting,
  onSortingChange,
  tableId,
  selectedView,
  columnsState = [],
  onColumnsChange,
  customFields,
  hasActiveFilters = false,
  onClearFilters,
  onClearSearch,
  customEmptyState,
  onEmptyChange,
  className,
  viewReady = true
}) => {
  const { t } = useTranslation();
  const { isFieldVisible, isLoaded: fieldsLoaded } = useFieldVisibility(
    EntityType.DEAL
  );
  const {
    isArr,
    label: arrMrrLabel,
    fieldName: arrOrMrrField,
    getValue: getArrOrMrrValue
  } = useArrMrr();
  // The two halves of the ARR/MRR pair. Sorting and sum aggregation stay on the
  // system half, where every row shares one currency and the total means
  // something; the terms half is display-only.
  const localArrOrMrrField = isArr ? "arrLocal" : "mrrLocal";
  const {
    visibleByDefault: termsColumnsVisibleByDefault,
    isResolved: currencyUsageResolved
  } = useTermsColumnsVisibleByDefault();
  // Memoized so DataTable's visibility state is not rebuilt on every render.
  const initialColumnVisibility = useMemo(
    () => ({
      [DealColumn.CLOSING_QUARTER]: false,
      ...Object.fromEntries([...DEALS_NEW_COLUMN_IDS].map((id) => [id, false])),
      // Only the pre-seed fallback — once Deals seeds columnsState it
      // drives visibility. Hidden until currency usage settles, so a
      // single-currency account never sees them flash in.
      ...Object.fromEntries(
        [...DEALS_TERMS_COLUMN_IDS].map((id) => [
          id,
          currencyUsageResolved && termsColumnsVisibleByDefault
        ])
      )
    }),
    [currencyUsageResolved, termsColumnsVisibleByDefault]
  );
  const currencyColumnLabels = useDealsCurrencyColumnLabels(
    arrMrrLabel,
    arrOrMrrField,
    customFields
  );
  const accountCurrency = useAccountCurrency();
  const currencyCodeResolvers = useMemo(
    () => dealsCurrencyCodeResolvers(accountCurrency, arrOrMrrField),
    [accountCurrency, arrOrMrrField]
  );
  const {
    drawerState: newDrawerState,
    openDrawer: newOpenDrawer,
    closeDrawer: newCloseDrawer
  } = useNewQuickViewDrawer();
  const { navigateToDetailedView } = useDealsNavigation();

  const newHandleOpenQuestionnaire = useCallback(
    (questionnaireId?: string) => {
      if (!newDrawerState.entityId) return;
      newCloseDrawer();
      navigateToDetailedView(
        newDrawerState.entityId,
        questionnaireId
          ? { openQuestionnaire: questionnaireId }
          : { [TAB_SEARCH_PARAM]: getTabSlug(TabOption.STAGE_WORKFLOWS) }
      );
    },
    [newDrawerState.entityId, newCloseDrawer, navigateToDetailedView]
  );

  const sortParams = useMemo(() => {
    const field = resolveSortField(sorting, DEALS_SORT_FIELD_BY_COLUMN);
    if (!field) return undefined;
    return { field, asc: !sorting[0].desc };
  }, [sorting]);

  // Grouping the aggregations by the field the table groups by makes one
  // response describe every group: the server returns a row per distinct value
  // with that group's own count and sums, over the whole filtered set.
  const groupField = groupBy?.[0];
  // Linked names group on the id they resolve from and a date bucket on its
  // date; an option the API can't group on groups the loaded rows.
  const groupAggregation = dealGroupAggregation(groupField);
  const aggregationsGroupField = groupAggregation?.field ?? "status";

  const {
    entities: deals,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    isError,
    refetch,
    total,
    aggregations,
    isAggregationsLoading,
    isAggregationsError
  } = useFilteredDeals(
    filters,
    searchTerm ? { term: searchTerm } : undefined,
    aggregationsGroupField, // Use to enforce aggregations
    undefined,
    sortParams ? sortParams.field : arrOrMrrField,
    sortParams ? sortParams.asc : true,
    false,
    viewReady,
    ComputedFields.ALL,
    groupAggregation?.function
  );

  const ownerIds = useMemo(() => {
    const ids = new Set<string>();
    deals.forEach((deal) => {
      if (deal.managedBy) ids.add(deal.managedBy);
    });
    // Grouping by owner names a group per owner in the whole filtered set, so
    // the names have to be fetched for owners whose rows are not loaded yet.
    if (aggregationsGroupField === DealColumn.MANAGED_BY) {
      groupFieldValues(
        aggregations as GenericAggregations<string> | undefined
      ).forEach((ownerId) => ids.add(ownerId));
    }
    return Array.from(ids);
  }, [deals, aggregations, aggregationsGroupField]);
  useEffect(() => {
    if (!isLoading) onEmptyChange?.(isEmpty(deals) && !isError);
  }, [isLoading, isError, deals, onEmptyChange]);

  const { users: ownerUsers } = useUsers(ownerIds);

  // Grouping by company names a group per company in the whole filtered set,
  // so the names have to come from the summaries' ids too — a company whose
  // rows are not loaded yet has no name anywhere else on the page.
  const groupCompanyIds = useMemo(() => {
    if (groupField !== DealColumn.COMPANY_NAME) return [];
    return groupFieldValues(
      aggregations as GenericAggregations<string> | undefined
    );
  }, [groupField, aggregations]);

  const { entities: groupCompanies } = useCompanies(groupCompanyIds, {
    enabled: groupCompanyIds.length > 0
  });

  const companyNameById = useMemo(() => {
    const names: Record<string, string> = {};
    deals.forEach((deal) => {
      if (deal.companyId && deal.companyName) names[deal.companyId] = deal.companyName;
    });
    groupCompanies.forEach((company) => {
      if (company?.id && company.name) names[company.id] = company.name;
    });
    return names;
  }, [deals, groupCompanies]);

  // The product catalog is account configuration, so every product group can be
  // named without waiting for one of its rows.
  const { data: productsData } = useProducts();
  const productNameById = useMemo(() => {
    const names: Record<string, string> = {};
    (productsData?.products ?? []).forEach((product) => {
      names[product.id] = product.name;
    });
    return names;
  }, [productsData]);
  const ownersDataMap: Dictionary<UserData> = useMemo(
    () => keyBy(ownerUsers.filter(Boolean), "id"),
    [ownerUsers]
  );

  // contactId is a SYSTEM People field with no API-resolved name — resolve client-side only when grouping.
  const isGroupedByContact = groupBy.includes(CONTACT_ID_FIELD);

  const contactIds = useMemo(() => {
    if (!isGroupedByContact) return EMPTY_IDS;
    const ids = new Set<string>();
    deals.forEach((deal) => {
      if (deal.contactId && !isReservedPersonId(deal.contactId))
        ids.add(deal.contactId);
    });
    return Array.from(ids);
  }, [deals, isGroupedByContact]);

  const contactPeopleOptions = useMemo(
    () => ({ enabled: isGroupedByContact && contactIds.length > 0 }),
    [isGroupedByContact, contactIds.length]
  );
  const { entities: contactPeople } = usePeople(
    contactIds,
    contactPeopleOptions
  );
  const contactsDataMap: Dictionary<PersonData> = useMemo(
    () => keyBy(contactPeople.filter(Boolean) as PersonData[], "id"),
    [contactPeople]
  );

  const contactFieldLabel = useMemo(() => {
    const field = customFields?.find(
      (customField) =>
        camelizeFieldKey(customField.fieldKey) === CONTACT_ID_FIELD
    );
    return field?.label ?? t("deals.createModal.contactId.label");
  }, [customFields, t]);

  const resetDealsInfinite = useResetFilteredDeals();

  useEffect(() => {
    if (resetPagination) {
      resetDealsInfinite();
      onPaginationReset?.();
    }
  }, [resetPagination, onPaginationReset]);

  const { data: dealStagesData } = useDealStages();
  const dealStages = useMemo(
    () => dealStagesData?.stages || [],
    [dealStagesData?.stages]
  );

  const dealStagesMap = useMemo(() => keyBy(dealStages, "id"), [dealStages]);

  const stageOrderById = useMemo(
    () => new Map(dealStages.map((stage) => [stage.id, stage.stageOrder])),
    [dealStages]
  );

  // Get selected columns from columnsState (updated by handleColumnsChange)
  // Fall back to temp view config if columnsState is empty (initial load)
  const selectedColumns = useMemo(() => {
    if (columnsState.length > 0) {
      return columnsState;
    }
    const tempView = getTempView(selectedView);
    return tempView?.config?.columns || [];
  }, [columnsState, selectedView]);

  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage && !isError) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, isError, fetchNextPage]);

  const onRowClick = useCallback(
    (deal: DealData) => {
      newOpenDrawer(deal.id, EntityType.DEAL);
    },
    [newOpenDrawer]
  );

  const transformedDeals = useMemo(
    () => addClosingQuarterGroup(deals),
    [deals]
  );

  const groupSummaryFields = useMemo(
    () => [
      arrOrMrrField,
      `committed${capitalize(arrOrMrrField)}`,
      "openForDays"
    ],
    [arrOrMrrField]
  );

  const groupSummaries = useMemo(() => {
    if (!supportsGroupSummaries(groupField)) return undefined;
    const byFieldValue = buildGroupSummaries(
      aggregations as GenericAggregations<string> | undefined,
      groupSummaryFields,
      isAggregationsLoading
    );
    // The server keys a date bucket exactly as the table does; a linked id it
    // grouped on becomes the name the column renders. An id that has no name
    // yet keeps its own key, where it matches no group rather than folding into
    // the wrong one.
    switch (groupField) {
      case DealColumn.COMPANY_NAME:
        return rekeyGroupSummaries(
          byFieldValue,
          (companyId) => companyNameById[companyId] ?? companyId
        );
      case DealColumn.PRODUCT_NAME:
        return rekeyGroupSummaries(
          byFieldValue,
          (productId) => productNameById[productId] ?? productId
        );
      default:
        return byFieldValue;
    }
  }, [
    aggregations,
    companyNameById,
    groupField,
    groupSummaryFields,
    isAggregationsLoading,
    productNameById
  ]);

  // Stage and status are account configuration, known before a single row
  // arrives, so every one of them can be on screen from the first paint —
  // including the ones the summaries came back empty for. Other group fields
  // take their values from the summaries, which is every value the filtered
  // set actually contains.
  const knownGroups = useMemo(() => {
    if (groupField === DealColumn.STAGE) {
      return [...dealStages]
        .sort((a, b) => a.stageOrder - b.stageOrder)
        .map((stage) => ({ value: stage.id, label: stage.name }));
    }
    if (groupField === DealColumn.STATUS) {
      return Object.values(DealStatus)
        .filter((value): value is DealStatus => typeof value === "number")
        .map((status) => ({
          value: status,
          label: t(
            getEnumTKey(
              "stakeholders.dealsModal.table",
              DealColumn.STATUS,
              DealStatus,
              status
            )
          )
        }));
    }
    if (groupField === DealColumn.AE_CONFIDENCE_LEVEL) {
      return [
        ...Object.values(DealConfidenceLevel)
          .filter(
            (value): value is DealConfidenceLevel => typeof value === "number"
          )
          .map((level) => ({
            value: level,
            label: t(getEnumTKey("deals", "levels", DealConfidenceLevel, level))
          })),
        { value: "", label: t("tables.noValue") }
      ];
    }
    if (groupField === DealColumn.MANAGED_BY && groupSummaries) {
      // Stable sort: owners keep the summaries' order, the unowned group goes last.
      return Object.keys(groupSummaries)
        .sort(
          (firstUserId, secondUserId) =>
            Number(firstUserId === "") - Number(secondUserId === "")
        )
        .map((userId) => ({
          value: userId,
          label: userId
            ? getFullName(ownersDataMap[userId] ?? {}) ||
              t("tables.notAvailable")
            : t("tables.noValue")
        }));
    }
    if (groupAggregation?.function && groupSummaries) {
      const bucket = parseDateBucketId(groupField ?? "")?.bucket ?? "month";
      return Object.keys(groupSummaries)
        .sort(
          (firstKey, secondKey) =>
            (dateBucketRank(firstKey, bucket) ?? Number.MAX_SAFE_INTEGER) -
            (dateBucketRank(secondKey, bucket) ?? Number.MAX_SAFE_INTEGER)
        )
        .map((key) => ({
          value: key,
          label: key ? formatDateBucketLabel(key, bucket) : t("tables.noValue")
        }));
    }
    if (
      (groupField === DealColumn.COMPANY_NAME ||
        groupField === DealColumn.PRODUCT_NAME) &&
      groupSummaries
    ) {
      // A key that is still an unresolved id names no group on screen yet, so
      // it is left out rather than shown as a raw id.
      const resolvedNames = new Set(
        Object.values(
          groupField === DealColumn.COMPANY_NAME
            ? companyNameById
            : productNameById
        )
      );
      return Object.keys(groupSummaries)
        .filter((key) => key === "" || resolvedNames.has(key))
        .sort((firstName, secondName) =>
          firstName === ""
            ? 1
            : secondName === ""
              ? -1
              : firstName.localeCompare(secondName)
        )
        .map((name) => ({
          value: name,
          label: name || t("tables.noValue")
        }));
    }
    return undefined;
  }, [
    groupField,
    groupAggregation,
    dealStages,
    groupSummaries,
    ownersDataMap,
    companyNameById,
    productNameById,
    t
  ]);

  // Stage follows the account's pipeline and status its own order; DataTable applies whichever is grouped, and ranks date buckets itself.
  const groupRankResolvers = useMemo<GroupRankResolvers<DealData>>(
    () => ({
      [DealColumn.STAGE]: (deal: DealData) =>
        deal.stage === null ? null : (stageOrderById.get(deal.stage) ?? null),
      [DealColumn.STATUS]: statusRank,
      [DealColumn.AE_CONFIDENCE_LEVEL]: (deal: DealData) =>
        deal.aeConfidenceLevel ?? null,
      // A KPI's groups follow its score ladder; they count the loaded rows only,
      // so none is declared up front.
      ...Object.fromEntries(
        KPI_COLUMNS.map(({ column }) => [
          column,
          (deal: DealData) =>
            scoreRank(deal[column as keyof DealData] as string | null)
        ])
      )
    }),
    [stageOrderById]
  );

  const alertsCell = useCallback((info: any) => {
    const dealId = info.row.original?.id;
    const alerts = info.getValue();
    return <AlertsIndication dealId={dealId} alerts={alerts} />;
  }, []);

  const columns = useMemo(() => {
    const columnDefs: ColumnDef<DealData, any>[] = [
      {
        header: t(`deals.${DealColumn.NAME}`),
        accessorKey: DealColumn.NAME,
        cell: (info) => (
          <TableNameCell info={info} entityType={EntityType.DEAL} />
        )
      },
      {
        header: t(`deals.${DealColumn.COMPANY_NAME}`),
        accessorKey: DealColumn.COMPANY_NAME,
        cell: (info) =>
          info.row.original?.companyName || t("tables.notAvailable")
      },
      {
        header: t(`deals.${DealColumn.PRODUCT_NAME}`),
        accessorKey: DealColumn.PRODUCT_NAME,
        cell: (info) =>
          info.row.original?.productName || t("tables.notAvailable")
      },
      {
        header: currencyColumnLabels.get(arrOrMrrField) ?? arrMrrLabel,
        accessorKey: arrOrMrrField,
        sortingFn: monetarySortingFn,
        meta: {
          aggregate: "sum",
          currencyGroupCode: currencyCodeResolvers[arrOrMrrField]
        },
        cell: (info: CellContext<DealData, unknown>) => (
          <SystemCurrencyCell
            amount={
              (info.row.original[arrOrMrrField as keyof DealData] as
                | number
                | null
                | undefined) ?? null
            }
          />
        )
      },
      {
        id: localArrOrMrrField,
        header: currencyColumnLabels.get(localArrOrMrrField) ?? arrMrrLabel,
        accessorFn: (row: DealData) =>
          row[localArrOrMrrField as keyof DealData],
        size: 160,
        enableSorting: false,
        meta: { currencyGroupCode: currencyCodeResolvers[localArrOrMrrField] },
        cell: (info: CellContext<DealData, unknown>) => (
          <TermsCurrencyCell
            amount={(info.getValue() as number | null | undefined) ?? null}
            currency={info.row.original.contractsCurrency ?? null}
          />
        )
      },
      {
        header: t(`deals.${DealColumn.STATUS}`),
        accessorKey: DealColumn.STATUS,
        sortingFn: createEnumSortingFn(DealStatus),
        cell: (info: CellContext<DealData, DealStatus | null>) => {
          const valueKey = getEnumTKey(
            "stakeholders.dealsModal.table",
            DealColumn.STATUS,
            DealStatus,
            info.getValue() as DealStatus
          );
          const statusKey = info.getValue() as DealStatus;
          const label = tWithFallback(
            valueKey,
            info.row.original.statusResolved
          );
          if (info.row.getIsGrouped()) return label;
          return (
            <StatusBadge statusColor={dealStatusColors[statusKey]} isTable>
              {label}
            </StatusBadge>
          );
        }
      },
      {
        header: t(`deals.${DealColumn.STAGE}`),
        accessorKey: DealColumn.STAGE,
        cell: (info) => {
          const dealStageValue = dealStagesMap[info.getValue() as number];
          return (
            dealStageValue && <StageBadge stage={dealStageValue} isTable />
          );
        },
        size: 180
      },
      {
        header: () => (
          <IconContainer color="secondary" size="sm">
            <Alert />
          </IconContainer>
        ),
        accessorKey: DealColumn.ALERTS,
        size: 80,
        minSize: 50,
        cell: alertsCell,
        // Alerts is a computed field whose expression is an array of alert
        // objects, and Spanner rejects ORDER BY on an array — the request the
        // header issues fails at the database.
        enableSorting: false
      },
      {
        header: t(`deals.${DealColumn.EXPECTED_CLOSE_AT}`),
        accessorKey: DealColumn.EXPECTED_CLOSE_AT,
        meta: { groupByMonth: true },
        cell: (info) => {
          const value = info.getValue() as ISOStringFormat;
          return (
            <span>
              {value
                ? simpleDate(toLocalDate(value), "short", false, false, true)
                : ""}
            </span>
          );
        }
      },
      {
        header: t(`deals.${DealColumn.CLOSED_AT}`),
        accessorKey: DealColumn.CLOSED_AT,
        meta: { groupByMonth: true, localDay: true },
        cell: (info) => {
          const value = info.getValue() as ISOStringFormat;
          return (
            <span>
              {value
                ? simpleDate(
                    toCalendarDate(value),
                    "short",
                    false,
                    false,
                    true
                  )
                : ""}
            </span>
          );
        }
      },
      {
        header: t(`deals.${DealColumn.CLOSED_LOST_REASON_NAME}`),
        accessorKey: DealColumn.CLOSED_LOST_REASON_NAME,
        cell: (info) => info.getValue() || t("tables.notAvailable")
      },
      {
        header: t(`deals.${DealColumn.CLOSING_QUARTER}`),
        accessorKey: DealColumn.CLOSING_QUARTER,
        sortingFn: (rowA: any, rowB: any) =>
          quarterSortingFn(rowA, rowB, DealColumn.CLOSING_QUARTER)
      },
      ...KPI_COLUMNS.map(
        ({
          column,
          scoreType,
          size
        }): ColumnDef<DealData, string | null | undefined> => ({
          header: t(`deals.${column}`),
          accessorKey: column,
          cell: (info: CellContext<DealData, string | null | undefined>) => {
            const value = info.getValue();
            return (
              <>
                {value && (
                  <DealScoreBadge
                    scoreText={value}
                    scoreType={scoreType}
                    size={size}
                  />
                )}
              </>
            );
          }
        })
      ),
      {
        header: t(`deals.${DealColumn.DEAL_VALUE_AMOUNT}`),
        accessorKey: DealColumn.DEAL_VALUE_AMOUNT,
        sortingFn: monetarySortingFn,
        meta: {
          aggregate: "sum"
        },
        cell: (info: CellContext<any, any>) => {
          const { value, currencySign } = formatNumber(
            (info.getValue() as number) || 0,
            0,
            true,
            false
          );
          return <span>{`${currencySign}${value}`}</span>;
        }
      },
      {
        header: t(`deals.${DealColumn.AE_CONFIDENCE_LEVEL}`),
        accessorKey: DealColumn.AE_CONFIDENCE_LEVEL,
        cell: (info) => {
          const value = info.getValue() as DealConfidenceLevel;
          if (value) {
            return (
              <div className="flex items-center w-full max-w-full overflow-hidden h-full">
                <div className="flex-shrink-0 flex items-center w-fit mr-[6px]">
                  <ConfidenceLevelIcon value={value} />
                </div>
                <div className="w-0 flex-grow flex items-center">
                  <span className="inline-block max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
                    {tWithFallback(
                      getEnumTKey(
                        "deals",
                        "levels",
                        DealConfidenceLevel,
                        value
                      ),
                      info.row.original.aeConfidenceLevelResolved
                    )}
                  </span>
                </div>
              </div>
            );
          }
        }
      },
      {
        header: t(`deals.${DealColumn.MANAGED_BY}`),
        accessorKey: DealColumn.MANAGED_BY,
        cell: (info) =>
          ownersDataMap[info.getValue() as string]?.displayName || t("notFound")
      },
      {
        header: t(`tables.${DealColumn.CREATED_AT}`),
        accessorKey: DealColumn.CREATED_AT,
        meta: { groupByMonth: true, localDay: true },
        sortingFn: "datetime",
        cell: (info) =>
          info.getValue()
            ? simpleDate(toCalendarDate(info.getValue() as string), "short")
            : t("tables.notAvailable")
      },
      {
        header: t(`deals.${DealColumn.LAST_ACTIVITY}`),
        accessorKey: DealColumn.LAST_ACTIVITY,
        meta: { groupByMonth: true, localDay: true },
        cell: (info) =>
          info.getValue()
            ? simpleDate(toCalendarDate(info.getValue() as string), "short")
            : ""
      },
      {
        header: t(`deals.${DealColumn.NEXT_STEP_TEXT}`),
        accessorKey: DealColumn.NEXT_STEP_TEXT,
        cell: (info) => (info.getValue() as string | null | undefined) ?? ""
      },
      {
        id: DealColumn.CONTRACTS_CARR,
        meta: {
          currencyGroupCode: currencyCodeResolvers[DealColumn.CONTRACTS_CARR]
        },
        header:
          currencyColumnLabels.get(DealColumn.CONTRACTS_CARR) ??
          t(`deals.${DealColumn.CONTRACTS_CARR}`),
        accessorFn: (row: DealData) => row.contractsCarr,
        size: 160,
        cell: (info: CellContext<DealData, unknown>) => (
          <SystemCurrencyCell
            amount={(info.getValue() as number | null | undefined) ?? null}
          />
        )
      },
      {
        id: DealColumn.CONTRACTS_CARR_TERMS,
        meta: {
          currencyGroupCode:
            currencyCodeResolvers[DealColumn.CONTRACTS_CARR_TERMS]
        },
        header:
          currencyColumnLabels.get(DealColumn.CONTRACTS_CARR_TERMS) ??
          t(`deals.${DealColumn.CONTRACTS_CARR}`),
        accessorFn: (row: DealData) => row.contractsCarrLocal,
        size: 160,
        enableSorting: false,
        cell: (info: CellContext<DealData, unknown>) => (
          <TermsCurrencyCell
            amount={(info.getValue() as number | null | undefined) ?? null}
            currency={info.row.original.contractsCurrency ?? null}
          />
        )
      },
      {
        id: DealColumn.CONTRACTS_TCV,
        meta: {
          currencyGroupCode: currencyCodeResolvers[DealColumn.CONTRACTS_TCV]
        },
        header:
          currencyColumnLabels.get(DealColumn.CONTRACTS_TCV) ??
          t(`deals.${DealColumn.CONTRACTS_TCV}`),
        accessorFn: (row: DealData) => row.contractsTotalContractValue,
        size: 160,
        cell: (info: CellContext<DealData, unknown>) => (
          <SystemCurrencyCell
            amount={(info.getValue() as number | null | undefined) ?? null}
          />
        )
      },
      {
        id: DealColumn.CONTRACTS_TCV_TERMS,
        meta: {
          currencyGroupCode:
            currencyCodeResolvers[DealColumn.CONTRACTS_TCV_TERMS]
        },
        header:
          currencyColumnLabels.get(DealColumn.CONTRACTS_TCV_TERMS) ??
          t(`deals.${DealColumn.CONTRACTS_TCV}`),
        accessorFn: (row: DealData) => row.contractsTotalContractValueLocal,
        size: 160,
        enableSorting: false,
        cell: (info: CellContext<DealData, unknown>) => (
          <TermsCurrencyCell
            amount={(info.getValue() as number | null | undefined) ?? null}
            currency={info.row.original.contractsCurrency ?? null}
          />
        )
      },
      {
        id: DealColumn.CONTRACTS_ACV,
        meta: {
          currencyGroupCode: currencyCodeResolvers[DealColumn.CONTRACTS_ACV]
        },
        header:
          currencyColumnLabels.get(DealColumn.CONTRACTS_ACV) ??
          t(`deals.${DealColumn.CONTRACTS_ACV}`),
        accessorFn: (row: DealData) => row.contractsAnnualContractValue,
        size: 160,
        cell: (info: CellContext<DealData, unknown>) => (
          <SystemCurrencyCell
            amount={(info.getValue() as number | null | undefined) ?? null}
          />
        )
      },
      {
        id: DealColumn.CONTRACTS_ACV_TERMS,
        meta: {
          currencyGroupCode:
            currencyCodeResolvers[DealColumn.CONTRACTS_ACV_TERMS]
        },
        header:
          currencyColumnLabels.get(DealColumn.CONTRACTS_ACV_TERMS) ??
          t(`deals.${DealColumn.CONTRACTS_ACV}`),
        accessorFn: (row: DealData) => row.contractsAnnualContractValueLocal,
        size: 160,
        enableSorting: false,
        cell: (info: CellContext<DealData, unknown>) => (
          <TermsCurrencyCell
            amount={(info.getValue() as number | null | undefined) ?? null}
            currency={info.row.original.contractsCurrency ?? null}
          />
        )
      },
      ...mapSelectedCustomColumnsToTableColumns(
        selectedColumns,
        customFields,
        t,
        DEAL_COLUMN_IDS_OWNED_BY_BUILTINS,
        groupBy,
        DEALS_CURRENCY_FIELD_RESOLVERS,
        currencyColumnLabels,
        DEALS_TERMS_COLUMN_IDS,
        currencyCodeResolvers,
        true
      )
    ];
    const visibleColumnDefs = !fieldsLoaded
      ? [...columnDefs]
      : columnDefs.filter((col) => isFieldVisible(getColumnKey(col)));

    // A quarter or year grouping groups on a column of its own, built from the
    // date column it buckets — found among every column def, or the custom
    // fields, so it groups even while that date column is hidden.
    const dateBucket = groupBy[0] ? parseDateBucketId(groupBy[0]) : undefined;
    const bucketBase = dateBucket
      ? columnDefs.find((col) => getColumnKey(col) === dateBucket.baseId)
      : undefined;
    const bucketCustomField =
      dateBucket && !bucketBase
        ? customFields?.find(
            (field) => camelizeFieldKey(field.fieldKey) === dateBucket.baseId
          )
        : undefined;
    const bucketCustomGrouping = bucketCustomField
      ? customFieldDateGrouping(bucketCustomField)
      : undefined;
    if (
      dateBucket &&
      (getDateBucket(bucketBase?.meta) || bucketCustomGrouping) &&
      !visibleColumnDefs.some((col) => getColumnKey(col) === groupBy[0])
    ) {
      visibleColumnDefs.push(
        buildDateBucketColumn<DealData>({
          baseId: dateBucket.baseId,
          bucket: dateBucket.bucket,
          accessorFn: (row) =>
            (row as unknown as Record<string, unknown>)[dateBucket.baseId],
          localDay: isLocalDayColumn(bucketBase?.meta),
          groupDateOf: bucketBase
            ? (bucketBase.meta as DateBucketMeta | undefined)?.groupDateOf
            : bucketCustomGrouping?.groupDateOf,
          header: bucketBase?.header ?? bucketCustomField?.label
        })
      );
    }

    // contactId has no column def (SYSTEM People field excluded from column pipeline) — synthesize one for grouping; DataTable hides grouped columns so it never appears as a data column.
    const hasContactColumn = visibleColumnDefs.some(
      (col) => getColumnKey(col) === CONTACT_ID_FIELD
    );
    if (groupBy.includes(CONTACT_ID_FIELD) && !hasContactColumn) {
      return [
        ...visibleColumnDefs,
        {
          id: CONTACT_ID_FIELD,
          accessorKey: CONTACT_ID_FIELD,
          header: contactFieldLabel,
          cell: (info: CellContext<DealData, unknown>) => {
            const id = info.row.original.contactId;
            if (!id || isReservedPersonId(id)) return t("tables.notAvailable");
            return (
              getFullName(contactsDataMap[id] ?? {}) || t("tables.notAvailable")
            );
          }
        }
      ];
    }

    return visibleColumnDefs;
  }, [
    t,
    arrMrrLabel,
    arrOrMrrField,
    localArrOrMrrField,
    currencyColumnLabels,
    currencyCodeResolvers,
    dealStagesMap,
    ownersDataMap,
    selectedColumns,
    customFields,
    isFieldVisible,
    fieldsLoaded,
    alertsCell,
    groupBy,
    contactFieldLabel,
    contactsDataMap
  ]);

  // Aggregations over nothing are just zeroes — let the empty state stand alone.
  const showCustomEmptyState =
    !isLoading && isEmpty(deals) && !!customEmptyState;

  return (
    <>
      {showCustomEmptyState ? null : (
        <DealsBottomKPIBar
          aggregations={aggregations}
          totalDeals={total}
          isLoading={isLoading || isAggregationsLoading}
          isError={isAggregationsError}
          isRowsError={isError}
        />
      )}
      <DataTable
        key={tableId}
        dataTest="deals-table"
        columns={applyColumnMeta(columns)}
        // Rows arrive ordered by the API and no client-side re-sort may discard that, except the stable stage-progression reorder above.
        manualSorting
        emptyGroupLabel={t("tables.noValue")}
        initialSortBy={sorting}
        initialColumnVisibility={initialColumnVisibility}
        onSortingChange={onSortingChange}
        tableOptions={DEALS_TABLE_OPTIONS}
        groupRankResolvers={groupRankResolvers}
        data={transformedDeals}
        headerClassName="border-none"
        stickyLeftColumn
        resizableColumns
        className={className}
        isInfiniteScroll
        isLoadingMore={isFetchingNextPage}
        isDataLoading={isLoading || !viewReady}
        hasMore={hasNextPage}
        onLoadMore={handleLoadMore}
        loadingThreshold={100}
        groupBy={groupBy}
        groupingConfig={{
          typeLabel: t("deals.bottomKPI.total"),
          aggregationKey: arrOrMrrField,
          aggregationLabel: arrMrrLabel,
          formatAggregation: (value: number) => displayTotalCurrency(value),
          groups: knownGroups,
          groupSummaries,
          subtotalAggregations: [
            {
              key: "mrr",
              label: t("deals.bottomKPI.arrMrr", { arrMrr: arrMrrLabel }),
              aggregate: (rows) =>
                rows.reduce(
                  (acc, row) => acc + (getArrOrMrrValue(row) || 0),
                  0
                ),
              fromSums: (sums) => sums[arrOrMrrField] || 0,
              format: (value) => displayTotalCurrency(value)
            },
            {
              key: "committedMrr",
              label: t("deals.bottomKPI.committedArrMrr", {
                arrMrr: arrMrrLabel
              }),
              aggregate: (rows) =>
                rows
                  .filter(
                    (row) =>
                      row.aeConfidenceLevel === DealConfidenceLevel.COMMITTED
                  )
                  .reduce((acc, row) => acc + (getArrOrMrrValue(row) || 0), 0),
              fromSums: (sums) =>
                sums[`committed${capitalize(arrOrMrrField)}`] || 0,
              format: (value) => displayTotalCurrency(value)
            },
            {
              key: "avgMrr",
              label: t("deals.bottomKPI.avgArrMrr", {
                arrMrr: arrMrrLabel
              }),
              // Averages only rows carrying a value, not the whole group —
              // the same population fromSums divides by, and the reason an
              // empty group reads 0 rather than NaN.
              aggregate: (rows) => {
                const values = rows
                  .map((row) => getArrOrMrrValue(row))
                  .filter(
                    (value): value is number => typeof value === "number"
                  );
                if (values.length === 0) return 0;
                return (
                  values.reduce((acc, value) => acc + value, 0) / values.length
                );
              },
              fromSums: (sums, _count, counts) =>
                counts[arrOrMrrField]
                  ? (sums[arrOrMrrField] || 0) / counts[arrOrMrrField]
                  : 0,
              format: (value) => displayTotalCurrency(value)
            },
            {
              key: "avgTime",
              label: t("deals.bottomKPI.avgTime"),
              aggregate: (rows) => {
                const values = rows
                  .map((row) => row.openForDays)
                  .filter(
                    (value): value is number => typeof value === "number"
                  );
                if (values.length === 0) return 0;
                return Math.floor(
                  values.reduce((acc, value) => acc + value, 0) /
                    (values.length * 7)
                );
              },
              fromSums: (sums, _count, counts) =>
                counts.openForDays
                  ? Math.floor(
                      (sums.openForDays || 0) / (counts.openForDays * 7)
                    )
                  : 0,
              format: (weeks) => `${weeks} ${t("deals.bottomKPI.avgTimeUnits")}`
            }
          ]
        }}
        enableColumnReorder
        tableId={tableId}
        selectedView={selectedView}
        onColumnsChange={onColumnsChange}
        columnsState={columnsState}
        onRowClick={(row: Row<any>) => onRowClick(row.original)}
        filterOptions={filterOptions}
        filters={filters}
        setFilters={setFilters}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={onClearFilters}
        onClearSearch={onClearSearch}
        isError={isError}
        onRetry={refetch}
        customEmptyState={customEmptyState}
      />
      <NewQuickView
        isOpen={newDrawerState.isOpen}
        entityId={newDrawerState.entityId}
        entityType={EntityType.DEAL}
        onClose={newCloseDrawer}
        handleOpenQuestionnaire={newHandleOpenQuestionnaire}
      />
    </>
  );
};
export default DealsTable;
